import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import CloseIcon from "@mui/icons-material/Close";
import { connect } from "react-redux";
import API from "../services";
import "./AlertPopup.scss";
import { useTranslation } from "../hooks/useTranslation";
import { RootState } from "../models/RootState";
import { BannerNavigation } from "../store/authActions";

type AlertPromotionItem = {
  priority?: number;
  promotionId: string;
  promotionImage?: string;
  promotionTitle?: string;
  redirectionPath?: string;
};

type GetAlertPromotionsResponse = {
  promotions?: AlertPromotionItem[];
};

type DynamicTextColors = {
  pageTextColorClass?: string;
};

interface AlertPopupOwnProps {
  suppress?: boolean;
  onlyOnPaths?: string[];
  sessionKey?: string;
  ready?: boolean;
}

interface StateProps {
  dynamicTextColors?: DynamicTextColors | null;
}

type AlertPopupProps = AlertPopupOwnProps & StateProps;

const DEFAULT_SESSION_KEY = "alertHomePopupShown";

const hasNonEmptyImage = (item: AlertPromotionItem) =>
  !!item.promotionImage && item.promotionImage.trim() !== "";

const isPlainExternalUrl = (value: string): boolean => {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^https?:\/\//i.test(trimmed)) return true;
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/.*)?$/i.test(trimmed);
};

const normalizeExternalUrl = (raw: string): string => {
  const url = raw.trim();
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
};

const AlertPopup: React.FC<AlertPopupProps> = ({
  suppress = false,
  onlyOnPaths,
  sessionKey = DEFAULT_SESSION_KEY,
  ready = true,
  dynamicTextColors,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<AlertPromotionItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const hasFetchedRef = useRef(false);

  const pageTextColorClass = dynamicTextColors?.pageTextColorClass ?? "";

  const pathAllowed = useMemo(() => {
    if (!onlyOnPaths || onlyOnPaths.length === 0) return true;
    return onlyOnPaths.some((p) => pathname.startsWith(p));
  }, [pathname, onlyOnPaths]);

  const hasAnyImage = useMemo(
    () => items.some(hasNonEmptyImage),
    [items]
  );

  const isImageOnlyBanner = useMemo(
    () =>
      items.length > 0 &&
      items.every((item) => hasNonEmptyImage(item)),
    [items]
  );

  const rememberAndClose = useCallback(() => {
    sessionStorage.setItem(sessionKey, "true");
    setOpen(false);
  }, [sessionKey]);

  const handleBannerClick = useCallback(
    (banner: AlertPromotionItem) => {
      const redirectionPath = banner?.redirectionPath?.trim() ?? "";
      rememberAndClose();

      if (!redirectionPath) return;

      if (isPlainExternalUrl(redirectionPath)) {
        window.location.href = normalizeExternalUrl(redirectionPath);
        return;
      }

      BannerNavigation(redirectionPath, navigate);
    },
    [navigate, rememberAndClose]
  );

  const handleCloseClick = useCallback(() => {
    if (currentIndex < items.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      return;
    }
    rememberAndClose();
  }, [currentIndex, items.length, rememberAndClose]);

  useEffect(() => {
    if (suppress && open) {
      setOpen(false);
    }
  }, [suppress, open]);

  const getAlertInfo = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.post<GetAlertPromotionsResponse>(
        "/webapp/get-promotions",
        {
          Category: "ALERT",
        }
      );

      const promotions = res?.data?.promotions ?? [];
      if (!promotions.length) {
        hasFetchedRef.current = false;
        return;
      }

      setItems(promotions);
      setCurrentIndex(0);
      setOpen(true);
    } catch {
      hasFetchedRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!ready) return;

    const seen = sessionStorage.getItem(sessionKey) === "true";
    if (suppress || !pathAllowed || hasFetchedRef.current || seen) return;

    hasFetchedRef.current = true;

    getAlertInfo().catch(() => {
      hasFetchedRef.current = false;
    });
  }, [ready, pathAllowed, suppress, sessionKey, pathname, getAlertInfo]);

  const renderAlertContent = () => {
    if (!items || items.length === 0) return null;

    const item = items[currentIndex];
    if (!item) return null;

    const hasRedirect =
      !!item.redirectionPath && item.redirectionPath.trim() !== "";
    const hasImage = hasNonEmptyImage(item);
    const imageOnly = hasImage;

    return (
      <div
        className={`alert-popup-item mb-3 hover:bg-opacity-90 transition-all rounded-md ${
          imageOnly ? "alert-popup-item--image-only" : ""
        }`}
        onClick={() => handleBannerClick(item)}
        style={{
          cursor: hasRedirect ? "pointer" : "default",
        }}
      >
        {hasImage && (
          <div className="alert-popup-image-wrapper">
            <img
              src={item.promotionImage}
              alt={item.promotionTitle || ""}
              className="alert-popup-banner-img"
              onClick={(event) => {
                event.stopPropagation();
                handleBannerClick(item);
              }}
              style={{
                cursor: hasRedirect ? "pointer" : "default",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  if (!pathAllowed) return null;

  const isDarkText = pageTextColorClass === "dark-text";
  const inlineColor = isDarkText ? "#000000" : "#FFFFFF";

  return (
    <Dialog
      className="alert-popup"
      open={open}
      onClose={rememberAndClose}
      maxWidth={false}
      fullWidth={false}
      scroll="paper"
      PaperProps={{ className: "alert-popup-paper" }}
    >
      <div className="relative">
        <CloseIcon
          className={`alert-popup-close-outside ${pageTextColorClass} ${
            isDarkText ? "text-black" : "text-white"
          }`}
          style={{ color: inlineColor }}
          onClick={handleCloseClick}
        />

        {loading ? <div>{t("common.loading")}</div> : renderAlertContent()}
      </div>
    </Dialog>
  );
};

const mapStateToProps = (state: RootState): StateProps => ({
  dynamicTextColors: state.userDetails?.dynamicTextColors,
});

export default connect<StateProps, unknown, AlertPopupOwnProps, RootState>(
  mapStateToProps
)(AlertPopup);